import React from 'react';
import withMainLayout from 'layouts/main';
import dynamic from 'next/dynamic';
import {Grid, Row, Col} from '@/grid';
import Container from '@/container';
import {SectionPadding, SectionPaddingHalfTop, SectionPaddingHalfBottom} from '@/section';
import {H1, H2, P, PMarginSm} from '@/typography';
import {BreakVisibleXxl} from '@/breaks';
import {withGreen800, withBgNeutral200Rounded} from '@/hocs/withColors';
import {withMarginBottomZeroToMd} from '@/hocs/withSpacing';
import PictureWrapper from '@/ui/images-wrapper';
import {ListOlNumbered} from '@/ui/lists/list-icons';
import {GridLayout, GridItem} from '@/ui/grid/grid-layout';
import BoxGrid from '@/ui/box-grid';

const H1Green800 = withGreen800(H1);
const DivBgNeutral200Rounded = withBgNeutral200Rounded('div');
const DivMarginedMobile = withMarginBottomZeroToMd('div');
const imgPath = '/static/images/developers/background-check/';

const SubmitPartnerApplication = dynamic(
  import('@/content/developers/submit-partner-application'),
  {
    loading: () => null
  }
);

const seoMetas = {
  title: 'Background check integration | Workable developer partners',
  description:
    'Integrate your background checking service with Workable. Help your customers request and manage background checks directly through their recruiting pipeline.',
  canonical: 'https://www.workable.com/developers/partner-program/background-check'
};

const BackgroundCheckPartnerPage = props => (
  <>
    <SectionPaddingHalfBottom>
      <Container variant="section-padding-below-nav">
        <SectionPaddingHalfTop>
          <Grid fluid>
            <Row middle="lg">
              <Col xs={12} lg={6}>
                <H1Green800 className="heading-1-smaller">
                  Connect your background
                  <BreakVisibleXxl /> check service with Workable
                </H1Green800>
                <PMarginSm>
                  Background checks help companies hire with confidence. Connect your service with
                  Workable to make this step seamless.
                </PMarginSm>
                <PMarginSm>
                  Companies use Workable to track candidates through their entire hiring process.
                  Those who need background checks as candidates reach the offer stage can use your
                  service directly through Workable.
                </PMarginSm>
              </Col>
              <Col xs={12} lg={6}>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="background-check-hero"
                  imageTypeFallback="jpg"
                  alt="Connect your background check service with Workable - image"
                  fetchpriority="high"
                  loading="eager"
                  width={584}
                  height={624}
                />
              </Col>
            </Row>
          </Grid>
        </SectionPaddingHalfTop>
      </Container>
    </SectionPaddingHalfBottom>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={7}>
              <GridItem>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="connect-background-check"
                  imageTypeFallback="jpg"
                  alt="How customers connect Workable with background check services - image"
                />
              </GridItem>
            </Col>
            <Col xs={12} lg={5} first="xs" last="lg">
              <H2 className="heading-3">
                How customers connect Workable with background check services
              </H2>
              <PMarginSm>
                Workable users can activate your integration with a one-time setup:
              </PMarginSm>
              <DivMarginedMobile>
                <DivBgNeutral200Rounded>
                  <ListOlNumbered>
                    <li>They find it in the integrations list inside Workable</li>
                    <li>Existing users of both services follow simple on-screen instructions</li>
                    <li>New users see your logo, a description, and a link to learn more</li>
                  </ListOlNumbered>
                </DivBgNeutral200Rounded>
              </DivMarginedMobile>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={5}>
              <PMarginSm>
                After setup, when candidates reach the right stage, users can start a background
                check without leaving Workable. Your service becomes part of their regular hiring
                process.
              </PMarginSm>
            </Col>
            <Col xs={12} lg={6} lgOffset={1}>
              <GridLayout Col={1} autoRows>
                <GridItem>
                  <BoxGrid
                    backgroundColor="var(--brown50)"
                    Url="https://workable.readme.io/"
                    UrlCopy="View API documentation"
                    target="_blank"
                    minHeight={288}>
                    <PMarginSm className="heading-3">API docs</PMarginSm>
                    <P>
                      See how you could start to integrate your product or service with Workable
                    </P>
                  </BoxGrid>
                </GridItem>
              </GridLayout>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>
    <SubmitPartnerApplication />
  </>
);

export default withMainLayout(BackgroundCheckPartnerPage, {...seoMetas});

export {getSharedPageStaticProps as getStaticProps} from 'helpers/sharedPageStaticProps';
